/**
 * @file ユーザーの属性を導出するRecoilセレクター
 *
 * 複数のatom（planInfo, customerInfo, providerInfo, paymentInfo, fvnoProvideType）を
 * 組み合わせて、画面制御に必要なユーザー属性を導出する。
 * SNCC/FVNOの判定、回線開通状態、コンビニ請求判定、一括料金プラン判定等、
 * アプリケーション全体で広く参照される重要なセレクター。
 */
import { selector } from 'recoil';
import { Constants } from '@/utils/constants';
import { planInfoState } from '@/states/planInfoState';
import { customerInfoState } from '@/states/customerInfoState';
import { providerInfoState } from '@/states/providerInfoState';
import { paymentInfoState } from '@/states/paymentInfoState';
import { fvnoProvideTypeState } from '@/states/fvnoProvideTypeState';

/** ユーザー属性の導出型 */
export type UserStateType = {
  name: string; // 氏名
  isSncc: boolean; // SNCC
  isFvno: boolean; // SNCC以外
  isOpen: boolean; // 回線開通
  isCornerShop: boolean; // コンビニ請求
  isBulkPrice: boolean; // 一括料金プラン
  isExist: boolean; // 既存スキーム
  isOrix: boolean; // ORIXスキーム
  isTenGsPlan: boolean; // 10Gsプラン
};

/**
 * 複数のatomからユーザー属性を導出するRecoilセレクター。
 * 各ステートの値から、SNCC判定・回線開通判定・プラン判定等を行う。
 */
const userState = selector<UserStateType>({
  key: 'userState',
  get: ({ get }) => {
    const planInfo = get(planInfoState);
    const customerInfo = get(customerInfoState);
    const providerInfo = get(providerInfoState);
    const payment = get(paymentInfoState);
    const fvnoProvideType = get(fvnoProvideTypeState);

    // 各atomの値を組み合わせて、画面制御に必要なフラグを導出する
    return {
      name: `${customerInfo.last_name} ${customerInfo.first_name}`,
      isSncc: providerInfo.providerId === Constants.ProviderId.SNCC,
      isFvno: providerInfo.providerId !== Constants.ProviderId.SNCC,
      isOpen: !!planInfo.serviceStartDate,
      isCornerShop: payment.payment_type === Constants.PaymentMethod.CONVENIENCE_STORE.code,
      isBulkPrice: Constants.PlanInfo.planCode.BULK_PRICE.includes(planInfo.planCode),
      isExist: fvnoProvideType.fvno_type === Constants.FvnoType.EXIST,
      isOrix: fvnoProvideType.fvno_type === Constants.FvnoType.ORIX,
      isTenGsPlan: fvnoProvideType.teng_s_plan_flg === Constants.TenGsPlanFlg.YES,
    };
  },
});

export { userState };
